import React, { useState, useMemo, useCallback } from 'react';
import {
  Card,
  Col,
  Row,
  Typography,
  Tooltip,
  Modal,
  message,
  Spin,
} from 'antd';
import {
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import { ITEMS_PER_PAGE } from '../../utils/consts';
import { ProjectPresetsResponse } from 'osep-generator-service-client-v1';
import findImageByProjectNameOrType from '../../utils/findImageByProjectNameOrType';
import { Param } from '../../cliDataSchema';
import useDeleteProjectPresetsMutation from '../../hooks/mutations/useDeleteProjectPresetsMutation';

type Props = {
  presetsLoading: boolean;
  filteredPresets: ProjectPresetsResponse[];
  currentPage: number;
  rowHeights: number[];
  featureGroups: Record<string, Record<string, Param[]>>;
  cardRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
};

const ProjectPresetsCards: React.FC<Props> = ({
  presetsLoading,
  filteredPresets,
  currentPage,
  rowHeights,
  featureGroups,
  cardRefs,
}) => {
  const [showApiKeys, setShowApiKeys] = useState<{ [key: string]: boolean }>(
    {},
  );
  const { deleteProjectPresets } = useDeleteProjectPresetsMutation();

  const paginatedPresets = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredPresets.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredPresets, currentPage]);

  const toggleApiKeyVisibility = useCallback((projectId: string) => {
    setShowApiKeys((prevState) => ({
      ...prevState,
      [projectId]: !prevState[projectId],
    }));
  }, []);

  const handleDelete = useCallback(
    (projectId: string) => {
      Modal.confirm({
        title: 'Are you sure you want to delete this project?',
        content: 'This action cannot be undone.',
        okText: 'Delete',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            await deleteProjectPresets(projectId);
            message.success('The project has been successfully deleted.');
          } catch (error: unknown) {
            if (error instanceof Error) {
              message.error(`Error deleting project: ${error.message}`);
            } else {
              message.error(
                'Error deleting project: An unknown error occurred.',
              );
            }
          }
        },
      });
    },
    [deleteProjectPresets],
  );

  return (
    <Row gutter={[16, 16]}>
      {presetsLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            width: '100%',
          }}
        >
          <Spin />
        </div>
      ) : (
        paginatedPresets.map((project, index) => {
          const displayData = project.params.map((param) => ({
            ...param,
            isSecret: param.isSecret,
          }));
          const rowIndex = Math.floor(index / 4);
          const cardHeight = rowHeights[rowIndex] || 'auto';

          return (
            <Col key={project.id} xs={24} sm={24} md={12} lg={8} xl={6}>
              <Card
                title={
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography.Title
                        level={5}
                        style={{
                          maxWidth: 'calc(100% - 50px)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {project.name}
                      </Typography.Title>

                      <img
                        src={findImageByProjectNameOrType(project.type)}
                        alt={project.type}
                        style={{
                          width: 'auto',
                          height: 25,
                        }}
                      />
                    </div>

                    <Typography.Text type="secondary">
                      {project.type}
                    </Typography.Text>
                  </div>
                }
                style={{
                  minHeight: cardHeight,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
                ref={(el) => (cardRefs.current[index] = el)}
                actions={[
                  <Tooltip title="Delete project">
                    <DeleteOutlined onClick={() => handleDelete(project.id)} />
                  </Tooltip>,
                  <Tooltip title="Toggle secret visibility">
                    <div onClick={() => toggleApiKeyVisibility(project.id)}>
                      {showApiKeys[project.id] ? (
                        <EyeOutlined />
                      ) : (
                        <EyeInvisibleOutlined />
                      )}
                    </div>
                  </Tooltip>,
                ]}
              >
                <Card.Meta
                  description={
                    <>
                      {displayData.map((param, paramIndex) => {
                        const paramWithDisplayName = Object.values(
                          featureGroups,
                        )
                          .flatMap((group) =>
                            Object.values(group).flatMap((params) =>
                              params.find(
                                (key) =>
                                  key.name === param.name &&
                                  key.osep_generator_ui_display_name,
                              ),
                            ),
                          )
                          .find(Boolean);

                        const displayKey = paramWithDisplayName
                          ? paramWithDisplayName.osep_generator_ui_display_name
                          : param.name;

                        const maskedValue =
                          param.isSecret && !showApiKeys[project.id]
                            ? '********'
                            : param.value;

                        return (
                          <div key={paramIndex}>
                            <strong>
                              {displayKey.charAt(0).toUpperCase() +
                                displayKey.slice(1)}
                              :
                            </strong>{' '}
                            {maskedValue}
                          </div>
                        );
                      })}
                    </>
                  }
                />
              </Card>
            </Col>
          );
        })
      )}
    </Row>
  );
};

export default ProjectPresetsCards;
